/**
 * The key that makes synced notes unreadable to everyone but you.
 *
 * markbird's sync stores documents in Firestore. Without this, they are stored
 * as text: the privacy policy says so, and it is the honest answer — but it is
 * also the one thing the apps people compare markbird to (Joplin, Notesnook,
 * Standard Notes) do differently by default. With a passphrase, the server
 * holds ciphertext and nothing else.
 *
 * Choices worth stating:
 *
 *  - **PBKDF2-SHA256, 600,000 iterations.** WebCrypto offers no Argon2, and
 *    600k is the OWASP figure for PBKDF2-SHA256 in 2023 and after. Deriving
 *    takes a noticeable moment on a phone, which is the point.
 *  - **AES-GCM**, a fresh 96-bit nonce per write. GCM authenticates as well as
 *    encrypts, so a record altered in the database fails to decrypt rather than
 *    decrypting into something else.
 *  - The derived key is **non-extractable** and kept as a `CryptoKey`. It can be
 *    stored (IndexedDB clones it) without the passphrase ever being written
 *    down — so a reload does not ask again, and a stolen database file yields
 *    no key material.
 */

const ITERATIONS = 600_000;
const SALT_BYTES = 16;
const IV_BYTES = 12;
/** What a correct passphrase must decrypt to; see `makeVerifier`. */
const VERIFIER_TEXT = 'markbird-vault-v1';

export interface VaultKeyRecord {
  /** Base64 salt, stored with the account so another device can derive the same key. */
  salt: string;
  /** Ciphertext of a known string: how a device tells a right passphrase from a wrong one. */
  verifier: Encrypted;
}

export interface Encrypted {
  /** Format version, so a future change can be told apart from corruption. */
  v: 1;
  iv: string;
  data: string;
}

function subtle(): SubtleCrypto {
  const api = globalThis.crypto?.subtle;
  if (!api) throw new Error('This browser has no Web Crypto, so notes cannot be encrypted.');
  return api;
}

export function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

export function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export function randomSalt(): string {
  return toBase64(crypto.getRandomValues(new Uint8Array(SALT_BYTES)));
}

/** Turn a passphrase into the key for this vault. Slow on purpose. */
export async function deriveKey(passphrase: string, salt: string): Promise<CryptoKey> {
  const material = await subtle().importKey(
    'raw',
    new TextEncoder().encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return subtle().deriveKey(
    {
      name: 'PBKDF2',
      salt: fromBase64(salt) as BufferSource,
      iterations: ITERATIONS,
      hash: 'SHA-256',
    },
    material,
    { name: 'AES-GCM', length: 256 },
    // Not extractable: the key can be kept for the next session, but never read
    // back out of the browser.
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function encryptText(key: CryptoKey, text: string): Promise<Encrypted> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const data = await subtle().encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    new TextEncoder().encode(text),
  );
  return { v: 1, iv: toBase64(iv), data: toBase64(new Uint8Array(data)) };
}

/** Decrypt, or null when the key is wrong or the record was altered. */
export async function decryptText(key: CryptoKey, payload: Encrypted): Promise<string | null> {
  if (payload?.v !== 1) return null;
  try {
    const plain = await subtle().decrypt(
      { name: 'AES-GCM', iv: fromBase64(payload.iv) as BufferSource },
      key,
      fromBase64(payload.data) as BufferSource,
    );
    return new TextDecoder().decode(plain);
  } catch {
    // Wrong key, or the ciphertext does not authenticate. Both mean: do not use it.
    return null;
  }
}

/** A new vault: a salt, and proof of the passphrase for the next device. */
export async function makeVaultKey(passphrase: string): Promise<{
  key: CryptoKey;
  record: VaultKeyRecord;
}> {
  const salt = randomSalt();
  const key = await deriveKey(passphrase, salt);
  return { key, record: { salt, verifier: await encryptText(key, VERIFIER_TEXT) } };
}

/** Whether a key in hand is the one this vault was made with. */
export async function keyOpensRecord(key: CryptoKey, record: VaultKeyRecord): Promise<boolean> {
  return (await decryptText(key, record.verifier)) === VERIFIER_TEXT;
}

/** The key for an existing vault, or null when the passphrase is wrong. */
export async function unlockVaultKey(
  passphrase: string,
  record: VaultKeyRecord,
): Promise<CryptoKey | null> {
  const key = await deriveKey(passphrase, record.salt);
  return (await decryptText(key, record.verifier)) === VERIFIER_TEXT ? key : null;
}
